import React from "react";
import { SwarovskiPageDataFrontend, SwarovskiPageDataTemp, type NewsletterTranslation } from "@swo/storefront";
import { SwarovskiOptikPage } from "@swo/sitemap-service";
import { PageRenderer } from "./page-renderer";

import { CartPageLayout } from "../../layouts/shop-layouts/cart-page-layout/cart-page-layout";
import { ProductDetailPageLayout } from "../../layouts/product-detail-page-layout/product-detail-page-layout";
import { PurchaseConfirmationPageLayout } from "../../layouts/shop-layouts/purchase-confirmation-page/purchase-confirmation-page-layout";
import { CheckoutPageLayout } from "../../layouts/shop-layouts/checkout-page-layout/checkout-page-layout";
import { ProductFamilyPageLayout } from "../../layouts/product-family-page-layout/product-family-page-layout";
import { ProductComparisonPageLayout } from "../../layouts/product-comparison-page-layout/product-family-page-layout";
import { ProductListingPageLayout } from "../../layouts/product-listing-page/product-listing-page-layout";
import { StoryLayout } from "../../layouts/stories/story-page/story-layout";
import { VideoStoriesOverviewPageLayout } from "../../layouts/stories/video-stories-overview-page-layout/video-stories-overview-page-layout";
import { StoriesOverviewPageLayout } from "../../layouts/stories/stories-overview-page/stories-overview-page-layout";
import { StandardPageLayout } from "../../layouts/standard-page-layout/standard-page-layout";
import { VideoStoryLayout } from "../../layouts/stories/video-story-page-layout/video-story-layout";
import { PressReleasesPageLayout } from "../../layouts/press-releases-page-layout/press-releases-page-layout";
import { PressReleasePageLayout } from "../../layouts/press-releases-page-layout/press-release-page-layout";
import { SearchPageLayout } from "../../layouts/search-page-layout/search-page-layout";
import { EventPageLayout } from "../../layouts/events/event-page-layout/event-page-layout";
import { EventsOverviewPageLayout } from "../../layouts/events/events-overview-page-layout/events-overview-page-layout";
import { DealerPageLayout } from "../../layouts/dealer-locator/dealer-page-layout/dealer-page-layout";
import { NewsletterLandingPageLayout } from "../../layouts/newsletter-landing-page-layout/newsletter-landing-page-layout";
import { DealerLocatorPageLayout } from "../../layouts/dealer-locator/dealer-locator-page-layout/dealer-locator-page-layout";
import { IntendedUsePageLayout } from "../../layouts/intended-use-page-layout/intended-use-page-layout";
import type { ImageWithFocalPointType, PublicConfigurationT, VideoType } from "@swo/shared";
import type { PageNewsletterData } from "@swo/storefront/dist-types/repositories/contentful/graphql/types/pages/common";

export function SwarovskiOptikPageRenderer(props: {
  data: SwarovskiPageDataFrontend;
  mode: "preview" | "published";
  config: PublicConfigurationT;
}) {
  const { data, mode, config } = props;

  const sharedProps = { mode, config };

  switch (data.__typename) {
    case "PageStandardGlobal": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={StandardPageLayout} />;
    }
    case "PageStoriesOverviewGlobal": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={StoriesOverviewPageLayout} />
      );
    }
    case "PageVideoStoriesOverviewGlobal": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer
          {...sharedProps}
          data={dataOrError}
          pageAllButData={rest}
          Component={VideoStoriesOverviewPageLayout}
        />
      );
    }
    case "PageStoryGlobal": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={StoryLayout} />;
    }
    case "PageVideoStoryGlobal": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={VideoStoryLayout} />;
    }

    case "PageProductListingGlobal": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={ProductListingPageLayout} />
      );
    }

    case "ProductComparisonPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer
          {...sharedProps}
          data={dataOrError}
          pageAllButData={rest}
          Component={ProductComparisonPageLayout}
        />
      );
    }

    case "PageProductFamilyGlobal": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={ProductFamilyPageLayout} />
      );
    }

    case "CheckoutPage": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={CheckoutPageLayout} />;
    }

    case "PurchaseConfirmationPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer
          {...sharedProps}
          data={dataOrError}
          pageAllButData={rest}
          Component={PurchaseConfirmationPageLayout}
        />
      );
    }

    case "ProductDetailPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={ProductDetailPageLayout} />
      );
    }

    case "ProductVariantDetailPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={ProductDetailPageLayout} />
      );
    }

    case "CartPage": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={CartPageLayout} />;
    }

    case "DealerLocatorPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={DealerLocatorPageLayout} />
      );
    }

    case "DealerPage": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={DealerPageLayout} />;
    }

    case "NewsletterLandingPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer
          {...sharedProps}
          data={dataOrError}
          pageAllButData={rest}
          Component={NewsletterLandingPageLayout}
        />
      );
    }

    case "EventsOverviewPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={EventsOverviewPageLayout} />
      );
    }

    case "EventPage": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={EventPageLayout} />;
    }

    case "PressReleasesPage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={PressReleasesPageLayout} />
      );
    }

    case "PressReleasePage": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={PressReleasePageLayout} />
      );
    }

    case "SearchPage": {
      const { dataOrError, ...rest } = data;

      return <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={SearchPageLayout} />;
    }

    case "PageIntendedUseGlobal": {
      const { dataOrError, ...rest } = data;

      return (
        <PageRenderer {...sharedProps} data={dataOrError} pageAllButData={rest} Component={IntendedUsePageLayout} />
      );
    }

    default: {
      console.warn(`Encountered pagetype ${(data as any).__typename} which is not supported.`);
      const _: never = data;

      return _;
    }
  }
}

export type SwarovskiPageAllButData<
  PageType extends SwarovskiOptikPage,
  Data extends {
    newsletter: PageNewsletterData;
  },
> = Omit<SwarovskiPageDataTemp<PageType, Data>, "dataOrError">;
